import {
  contrastProblems, DERIVED_TOKENS, normaliseHex, storedCard, TAB_BAR_STYLES, MATERIALS, BACKGROUNDS, PAGE_IDS, PAGE_SECTIONS, STARTING_APPEARANCE_VIEW, TAB_ICONS, TAB_IDS, TAB_LABEL_MAX, TAB_ROUTES, THEME_TOKENS, WORDING,
  MAX_TABS, MIN_TABS, type AppearanceView, type TabBarStyle, type Material, type Background, type PageId, type TabIcon, type TabId, type ThemeColours, type WordingKey, storedWelcome, welcomeView,
  designContrastProblems, designVars, storedCardDesigns, type CardDesign } from '@tikkas/shared';

/*
 * The customer app's look and arrangement from the admin (CLAUDE.md §4, Saif 20 Oct 2026; docs/26): the theme in force
 * (colours for light and dark, the logo), the tabs, the sections of Card, Offers, Order and Account, and the key wording.
 * The rules live in @tikkas/shared; this reads GET /api/appearance (or the copy kept on the phone) leniently, so an odd
 * or broken answer never breaks the app: anything unreadable takes the starting look, and the guard rails hold whatever
 * arrives (readable colours, Account reachable, locked sections shown). Pure functions; lib/look.tsx applies them.
 */

export type Mode = 'light' | 'dark';
export type View = AppearanceView;
export const STARTING = STARTING_APPEARANCE_VIEW;

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const clean = (v: unknown) => (typeof v === 'string' ? v.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim() : '');

/* ---------- the theme ---------- */

function colours(raw: unknown, mode: Mode): ThemeColours {
  const o = obj(raw);
  return Object.fromEntries(THEME_TOKENS.map((t) => [t.key, normaliseHex(o[t.key]) ?? t[mode]])) as ThemeColours;
}

/** Colours for both modes; a mode whose text the server should never have let through (under 4.5:1) is drawn in the starting colours. */
export function readColours(light: unknown, dark: unknown): { light: ThemeColours; dark: ThemeColours } {
  const out = { light: colours(light, 'light'), dark: colours(dark, 'dark') };
  const bad = new Set(contrastProblems({ ...out, pass: STARTING.theme.pass }).map((p) => p.mode));
  return { light: bad.has('light') ? STARTING.theme.light : out.light, dark: bad.has('dark') ? STARTING.theme.dark : out.dark };
}

/** A logo is one of ours, served by the API under /api/appearance/logos/<key> (the key URL-encoded); anything else draws the built-in one. */
const logoPath = (v: unknown) => (typeof v === 'string' && /^\/api\/appearance\/logos\/[\w.%-]{1,300}$/.test(v) && !/\.\.|%2e%2e/i.test(v) ? v : null);

/* ---------- tabs ---------- */

function readTabs(raw: unknown): View['tabs'] {
  if (!Array.isArray(raw)) return STARTING.tabs;
  const out: View['tabs'] = [];
  for (const x of raw) {
    const r = obj(x);
    const id = r.id as TabId;
    if (!(TAB_IDS as readonly string[]).includes(id) || out.some((t) => t.id === id)) continue;
    const label = clean(r.label);
    const start = STARTING.tabs.find((t) => t.id === id);
    out.push({ id, label: label && label.length <= TAB_LABEL_MAX ? label : start?.label ?? (id === 'catering' ? 'Catering' : id), icon: (TAB_ICONS as readonly string[]).includes(r.icon as string) ? (r.icon as TabIcon) : start?.icon ?? 'people' });
    if (out.length === MAX_TABS) break;
  }
  return out.length >= MIN_TABS ? out : STARTING.tabs;
}

/* ---------- page sections ---------- */

/** A page's sections in the order sent; unknown and repeated ones dropped; the locked ones (the card, the menu, consent, legal, sign-out, deletion) always there. */
export function readSections(page: PageId, raw: unknown): string[] {
  const defs = PAGE_SECTIONS[page];
  if (!Array.isArray(raw)) return STARTING.pages[page];
  const list = [...new Set(raw.filter((s): s is string => typeof s === 'string' && defs.some((d) => d.id === s)))];
  for (const d of defs) {
    if (!d.locked || list.includes(d.id)) continue;
    // Put it back where it starts: before the first section that starts after it, else at the end.
    const after = defs.slice(defs.indexOf(d) + 1).map((x) => x.id);
    const at = list.findIndex((s) => after.includes(s));
    if (at < 0) list.push(d.id);
    else list.splice(at, 0, d.id);
  }
  return list;
}

/* ---------- wording ---------- */

function readWording(raw: unknown): View['wording'] {
  const o = obj(raw);
  return Object.fromEntries(WORDING.map((w) => {
    const v = clean(o[w.key]);
    return [w.key, v && v.length <= w.max ? v : w.default];
  })) as View['wording'];
}

/* ---------- the card's design ---------- */

/**
 * A card design as the server sends it, read leniently: anything unreadable, or an ink the server should never have let
 * through (under 4.5:1 on a stop), draws the starting design instead, so the numbers always read.
 */
export function readDesign(raw: unknown): CardDesign {
  const read = storedCardDesigns({ designs: [raw], defaultId: typeof obj(raw).id === 'string' ? obj(raw).id : '' });
  const d = read.designs[0];
  return d && !designContrastProblems(d).length && d.id === String(obj(raw).id ?? '').toLowerCase() ? d : STARTING.cardDesign;
}

/**
 * The design as CSS variables on the card itself (the shared CARD_CSS and styles.css .bankcard read them): the gradient,
 * the ink and its softer form (scoped as --on-yellow, so everything inside the card follows), the key colour, the logo
 * colour, the rim, the glint and the animation's intensity. Nothing outside the card changes. `logoUrl` is the image the
 * masked logo takes (the theme's upload, or the built-in wordmark).
 */
export const designStyle = (d: CardDesign, logoUrl = '/logo.webp'): Record<string, string> => designVars(d, logoUrl);

/* ---------- the whole answer ---------- */

/** The welcome as the API sends it (imageUrl) read back into the stored shape (image), so one lenient reader serves both. */
function welcomeStored(raw: unknown): unknown {
  const o = obj(raw);
  return { ...o, cards: Array.isArray(o.cards) ? o.cards.map((c) => { const r = obj(c); return { ...r, image: typeof r.imageUrl === 'string' ? r.imageUrl : null }; }) : [] };
}

export function readView(raw: unknown): View {
  const r = obj(raw);
  const th = obj(r.theme);
  const logo = obj(th.logo);
  const tabs = readTabs(r.tabs);
  const accountTab = tabs.some((t) => t.id === 'account');
  const pages = obj(r.pages);
  const next = typeof r.nextChangeAt === 'string' && !Number.isNaN(Date.parse(r.nextChangeAt)) ? r.nextChangeAt : null;
  return {
    version: clean(r.version).slice(0, 80) || 'default',
    theme: {
      id: clean(th.id).slice(0, 60) || 'everyday',
      name: clean(th.name).slice(0, 60) || 'Everyday',
      ...readColours(th.light, th.dark),
      logo: { light: logoPath(logo.light), dark: logoPath(logo.dark) },
      pass: STARTING.theme.pass, // the Wallet pass's colours are the pass's business (card-wallet), not the app's
      tabBar: (TAB_BAR_STYLES as readonly unknown[]).includes(th.tabBar) ? (th.tabBar as TabBarStyle) : STARTING.theme.tabBar,
      material: (MATERIALS as readonly unknown[]).includes(th.material) ? (th.material as Material) : STARTING.theme.material,
      background: (BACKGROUNDS as readonly unknown[]).includes(th.background) ? (th.background as Background) : STARTING.theme.background,
      motion: typeof th.motion === 'boolean' ? th.motion : STARTING.theme.motion,
    },
    nextChangeAt: next,
    tabs,
    // Account is always reachable: a tab, or the account button (Apple requires account deletion to be reachable).
    accountButton: accountTab ? r.accountButton !== false : true,
    pages: Object.fromEntries(PAGE_IDS.map((p) => [p, readSections(p, pages[p])])) as View['pages'],
    wording: readWording(r.wording),
    // The Card page's QR style (CLAUDE.md §4 "Card"): flip or front; anything else takes the start.
    card: storedCard(r.card),
    // The first-open welcome: the switch, tagline and cards; photos are already URLs here, so they pass through unchanged.
    welcome: welcomeView(storedWelcome(welcomeStored(r.welcome)), (k) => k),
    // The default card design (docs/26 §5.2): the join page's card; a signed-in customer's own comes with /api/me.
    cardDesign: readDesign(r.cardDesign),
  };
}

/* ---------- using it ---------- */

export const say = (v: View, k: WordingKey): string => v.wording[k] ?? WORDING.find((w) => w.key === k)!.default;

/** The tabs that show: Catering only while its switch is on. */
export const visibleTabs = (v: View, cateringOn: boolean) => v.tabs.filter((t) => t.id !== 'catering' || cateringOn);
export const accountIsTab = (v: View) => v.tabs.some((t) => t.id === 'account');
/** Where the app opens: Home, or the first tab that shows when Home isn't one. */
export function startPath(v: View, cateringOn: boolean): string {
  const tabs = visibleTabs(v, cateringOn);
  return tabs.some((t) => t.id === 'home') || !tabs[0] ? '/' : TAB_ROUTES[tabs[0].id];
}
const SCREEN_TAB: Record<string, TabId> = { home: 'home', menu: 'menu', build: 'menu', activity: 'home', cardback: 'home', account: 'account', cater: 'catering', corders: 'catering', corder: 'catering', chelp: 'catering' };
/** The tab a screen belongs to, for the highlighted tab. */
export const tabForScreen = (screen: string): TabId | null => SCREEN_TAB[screen] ?? null;
/** The main screens: the tab bar shows on each of them, whether or not it's a tab today. */
export const MAIN_SCREENS = ['home', 'menu', 'account'];

/** The theme's logo for a mode (the light one stands in for dark), or null for the built-in wordmark. */
/** The theme's logo: the app is light only (Saif, 22 Oct 2026), so the light one; the admin's dark colours and logo are not drawn. */
export const logoFor = (v: View): string | null => v.theme.logo.light;

const same = (a: ThemeColours, b: ThemeColours) => THEME_TOKENS.every((t) => a[t.key] === b[t.key]);
/** Today's own colours: the stylesheet's values stay exactly as they are. */
export const isStartingTheme = (v: View) => same(v.theme.light, STARTING.theme.light) && same(v.theme.dark, STARTING.theme.dark);

/** The app's own extras worked out from the brand colour: the light and deep ends of the card's gradient. */
const APP_DERIVED = [
  { key: 'brand-hi', css: 'color-mix(in srgb, var(--yellow) 70%, #ffffff)' },
  { key: 'brand-lo', css: 'color-mix(in srgb, var(--yellow) 88%, #000000)' },
];

/**
 * The theme as one stylesheet: every light token on :root, then the see-through colours worked out from them
 * (DERIVED_TOKENS). `:root:root:root` outranks every :root rule in the base stylesheet wherever it loads, so no
 * !important. Nothing at all for today's colours with the built-in logo. The app is light only (Saif, 22 Oct 2026):
 * the theme's dark colours arrive from the API but are never drawn.
 */
export function themeCss(v: View): string {
  const block = (sel: string, vars: [string, string][]) => (vars.length ? `${sel}{${vars.map(([k, val]) => `--${k}:${val}`).join(';')}}` : '');
  const filter: [string, string][] = v.theme.logo.light ? [['logo-filter', 'none']] : [];
  if (isStartingTheme(v)) return block(':root:root:root', filter);
  const derived = [...DERIVED_TOKENS, ...APP_DERIVED].map((d): [string, string] => [d.key, d.css]);
  return block(':root:root:root', [...THEME_TOKENS.map((t): [string, string] => [t.key, v.theme.light[t.key]]), ...derived, ...filter]);
}

/**
 * The look to draw from the copy kept on the phone. A special whose end (nextChangeAt) has passed while the phone was
 * offline gives way to the last everyday theme seen, so a Diwali look doesn't linger into November.
 */
export function viewForNow(kept: View, everyday: View['theme'] | null, now: Date): View {
  if (kept.theme.id === 'everyday' || !kept.nextChangeAt || Date.parse(kept.nextChangeAt) > now.getTime()) return kept;
  return { ...kept, theme: everyday ?? STARTING.theme };
}
